import { PhoneCampaign } from '@hc-frontend/core-types';
import { Button, usePhoneCampaign } from '@hc-frontend/core-ui';
import React from 'react';

import { Stepper } from './stepper';

export interface ACAQuizProps {
  incomeThreshold: number;
  redirectUrlDefault: string;
  redirectUrlMedU64: string;
}

interface ViewProps {
  setCurrentView: (
    view:
      | 'CTA'
      | 'ASKING_ENROLLED'
      | 'ASKING_AGE'
      | 'ASKING_INCOME'
      | 'CLICK_TO_CALL'
      | 'REDIRECT',
  ) => void;
  setHasMedicareMedicaid: (val: boolean) => void;
  setIsUnder64: (val: boolean) => void;
  setBelowThreshold: (val: boolean) => void;
  hasMedicareMedicaid?: boolean;
  incomeThreshold: number;
  phoneCampaign?: PhoneCampaign;
  redirectUrl?: string;
  belowThreshold?: boolean;
}

export default function ACAQuiz(props: ACAQuizProps) {
  const phoneCampaign = usePhoneCampaign();
  const [currentView, setCurrentView] = React.useState<
    | 'CTA'
    | 'ASKING_ENROLLED'
    | 'ASKING_AGE'
    | 'ASKING_INCOME'
    | 'CLICK_TO_CALL'
    | 'REDIRECT'
  >('CTA');

  const [hasMedicareMedicaid, setHasMedicareMedicaid] =
    React.useState<boolean>(false);
  const [isUnder64, setIsUnder64] = React.useState<boolean>(false);
  const [belowThreshold, setBelowThreshold] = React.useState<boolean>(false);

  const handleBackClick = () => {
    switch (currentView) {
      case 'ASKING_ENROLLED':
        setCurrentView('CTA');
        break;
      case 'ASKING_AGE':
        setCurrentView('ASKING_ENROLLED');
        break;
      case 'ASKING_INCOME':
        setCurrentView('ASKING_AGE');
        break;
    }
  };

  return (
    <div className="rounded-lg bg-white w-full border-[1px] block min-h-[300px]">
      {!['CLICK_TO_CALL', 'REDIRECT'].includes(currentView) && (
        <div className="mx-auto mt-[20px] w-[80%]">
          <Stepper
            total={3}
            current={
              currentView === 'ASKING_ENROLLED'
                ? 1
                : currentView === 'ASKING_AGE'
                  ? 2
                  : currentView === 'CTA'
                    ? 0
                    : 3
            }
          />
        </div>
      )}
      <div className="mx-auto text-center my-auto block font-[Arial] font-medium text-[black]">
        {currentView === 'CTA' && (
          <CTA
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            incomeThreshold={props.incomeThreshold}
          />
        )}
        {currentView === 'ASKING_ENROLLED' && (
          <ASKING_ENROLLED
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            incomeThreshold={props.incomeThreshold}
          />
        )}
        {currentView === 'ASKING_AGE' && (
          <ASKING_AGE
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            hasMedicareMedicaid={hasMedicareMedicaid}
            incomeThreshold={props.incomeThreshold}
          />
        )}
        {currentView === 'ASKING_INCOME' && (
          <ASKING_INCOME
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            incomeThreshold={props.incomeThreshold}
          />
        )}
        {currentView === 'CLICK_TO_CALL' && phoneCampaign && (
          <CLICK_TO_CALL
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            phoneCampaign={phoneCampaign}
            belowThreshold={belowThreshold}
            incomeThreshold={props.incomeThreshold}
          />
        )}
        {currentView === 'REDIRECT' && (
          <REDIRECT
            setCurrentView={setCurrentView}
            setHasMedicareMedicaid={setHasMedicareMedicaid}
            setIsUnder64={setIsUnder64}
            setBelowThreshold={setBelowThreshold}
            incomeThreshold={props.incomeThreshold}
            redirectUrl={
              isUnder64 && hasMedicareMedicaid
                ? props.redirectUrlMedU64
                : props.redirectUrlDefault
            }
          />
        )}
        {!['CTA', 'REDIRECT', 'CLICK_TO_CALL'].includes(currentView) && (
          <div
            onClick={handleBackClick}
            className="ml-[8px] my-[8px] hover:underline w-fit text-left cursor-pointer"
          >
            Go back
          </div>
        )}
      </div>
    </div>
  );
}

const CTA = (props: ViewProps) => {
  return (
    <div className="my-[48px]">
      <h3 className="text-xl mb-[32px]">
        Answer just 3 questions to see if you qualify!
      </h3>
      <h3 className="italic text-xl mb-[32px]">
        4 out of 5 Americans qualify.
      </h3>
      <Button
        onClick={() => props.setCurrentView('ASKING_ENROLLED')}
        className="rounded-lg font-[Arial] font-medium"
        variant="primary"
      >
        See if I qualify
      </Button>
    </div>
  );
};

const ASKING_ENROLLED = (props: ViewProps) => {
  return (
    <div className="my-[48px] mx-[24px]">
      <h3 className="text-xl text-left mb-[32px]">
        Are you currently enrolled in Medicaid or Medicare?
      </h3>
      <Button
        onClick={() => {
          props.setCurrentView('ASKING_AGE');
          props.setHasMedicareMedicaid(false);
        }}
        className="rounded-lg mb-[12px] w-full transition-colors !text-center font-[Arial] font-medium"
        variant="primary-card"
      >
        <div className="w-full">No</div>
      </Button>
      <Button
        onClick={() => {
          props.setHasMedicareMedicaid(true);
          props.setCurrentView('ASKING_AGE');
        }}
        className="rounded-lg mb-[12px] w-full transition-colors bg-gray-50 hover:bg-gray-100 !text-center font-[Arial] font-medium"
        variant="text"
      >
        <div className="w-full text-[black]">Yes</div>
      </Button>
      <Button
        onClick={() => {
          props.setHasMedicareMedicaid(false);
          props.setCurrentView('ASKING_AGE');
        }}
        className="rounded-lg mb-[12px] w-full transition-colors bg-gray-50 hover:bg-gray-100 !text-center font-[Arial] font-medium"
        variant="text"
      >
        <div className="w-full text-[black]">Not Sure</div>
      </Button>
    </div>
  );
};

const ASKING_AGE = (props: ViewProps) => {
  return (
    <div className="my-[48px] mx-[24px]">
      <h3 className="text-xl text-left mb-[32px]">
        Are you currently under 64 years old?
      </h3>
      <Button
        onClick={() => {
          props.setIsUnder64(true);
          if (props.hasMedicareMedicaid) {
            props.setCurrentView('REDIRECT');
          } else {
            props.setCurrentView('ASKING_INCOME');
          }
        }}
        className="rounded-lg mb-[12px] w-full transition-colors !text-center font-[Arial] font-medium"
        variant="primary-card"
      >
        <div className="w-full">Yes</div>
      </Button>
      <Button
        onClick={() => {
          props.setIsUnder64(false);
          props.setCurrentView('REDIRECT');
        }}
        className="rounded-lg mb-[12px] w-full transition-colors bg-gray-50 hover:bg-gray-100 !text-center font-[Arial] font-medium"
        variant="text"
      >
        <div className="w-full text-[black]">No</div>
      </Button>
    </div>
  );
};

const ASKING_INCOME = (props: ViewProps) => {
  const formattedIncomeThreshold = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(props.incomeThreshold);

  return (
    <div className="my-[48px] mx-[24px]">
      <h3 className="text-xl text-left mb-[32px]">
        Do you make under {formattedIncomeThreshold} per year?
      </h3>
      <Button
        onClick={() => {
          props.setBelowThreshold(true);
          props.setCurrentView('CLICK_TO_CALL');
        }}
        className="rounded-lg mb-[12px] w-full transition-colors !text-center font-[Arial] font-medium"
        variant="primary-card"
      >
        <div className="w-full">Yes</div>
      </Button>
      <Button
        onClick={() => {
          props.setBelowThreshold(false);
          props.setCurrentView('CLICK_TO_CALL');
        }}
        className="rounded-lg mb-[12px] w-full transition-colors bg-gray-50 hover:bg-gray-100 !text-center font-[Arial] font-medium"
        variant="text"
      >
        <div className="w-full text-[black]">No</div>
      </Button>
    </div>
  );
};

const CLICK_TO_CALL = (props: ViewProps) => {
  return (
    <div className="my-[48px] mx-[24px]">
      <h3 className="text-xl text-center mb-[32px]">
        {props.belowThreshold ? 'Congratulations!' : "You're almost there!"}
      </h3>
      <h3 className="text-xl text-center mb-[32px]">
        {props.belowThreshold
          ? 'You are pre-qualified for subsidized health insurance! Call now to finish your registration!'
          : ' We just need a few more pieces of information. Call Now to Speak to a Licensed Agent!'}
      </h3>
      <Button
        onClick={() => props.setCurrentView('ASKING_INCOME')}
        className="rounded-lg mb-[12px] bg-[#3b82f6] hover:bg-[#335fb5] transition-colors !text-center font-[Arial] font-medium"
        variant="primary-card"
      >
        {props.phoneCampaign?.phoneNumber}
      </Button>
      <p>(This is a 100% FREE call. No obligation to enroll.)</p>
    </div>
  );
};

const REDIRECT = (props: ViewProps) => {
  return (
    <div className="my-[48px] mx-[24px]">
      <h3 className="text-xl text-center mb-[32px]">
        Thank You for Your Interest
      </h3>
      <h3 className="text-xl text-center mb-[32px]">
        Based on your responses, we will need gather a bit more information.
        Visit our full questionnaire to get started!
      </h3>
      <Button
        onClick={() =>
          props.redirectUrl
            ? (location.href = props.redirectUrl)
            : (location.href = 'https://www.healthcare.com')
        }
        className="rounded-lg mb-[12px] bg-[#3b82f6] hover:bg-[#335fb5] transition-colors !text-center font-[Arial] font-medium"
        variant="primary-card"
      >
        Visit Healthcare.com
      </Button>
    </div>
  );
};
